<template>
    <div class="ticket-filter-toolbar">
        <CommonFilterTool
            v-model="ticketFilter"
            :keep-state-on-query-params="false"
            :filter-meta="filterMetas"
            :label-formatter="filterLabelFormatter"
            @addFilter="addFilter"
            :showClearAll="false && isClearAllEnabled()"
            @applyFilterTemplate="applyFilterTemplate"
            :filter-templates="getFilterTemplates()"
            :selected-filter-template="getSelectedFilterTemplateName()"
            :hide-filters="false && this.selectedCustomFilterTemplate !== 'custom'"
            :show-add-button="true"
            :required-filters="getRequiredBreakdowns()"
        >
            <template slot="extra">
                <slot name="extra" />
            </template>
        </CommonFilterTool>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import moment from "moment";
import log2 from "../../../../utils/log2";
import CommonFilterTool from "../../Chatbot/pages/archive/CommonFilterTool.vue";

const log = log2("TicketFilterToolbar");

export default {
    name: "TicketFilterToolbar",
    components: { CommonFilterTool },
    props: {
        value: {
            type: Array,
            required: true,
        },
        statusList: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
    data() {
        return {
            defaultFilterTemplate: "this-week-open-tickets",
            filterMetas: [
                {
                    name: "dateFilter",
                    label: this.$t("ticket.ticketFilterList.creationDate"),
                    materialIcon: "event",
                    controlProps: {},
                    controlType: "date-range-picker",
                    defaultValue: {
                        timeSelect: "isoWeek",
                        startDate: null,
                        endDate: null,
                    },
                },
                {
                    name: "tags",
                    label: this.$t("ticket.ticketFilterList.label"),
                    materialIcon: "label",
                    controlType: "custom-picker",
                    controlProps: {
                        multiple: true,
                        idKey: "id",
                        labelKey: "label",
                        title: "Select Ticket Tag",
                    },

                    defaultValue: [],
                    options: () => this.ticketTags.map((x) => ({ id: x, label: x })),
                },
                {
                    name: "statusList",
                    label: this.$t("ticket.ticketFilterList.status"),
                    materialIcon: "info",
                    controlType: "custom-picker",
                    controlProps: {
                        multiple: true,
                        idKey: "id",
                        labelKey: "label",
                        title: "Select Ticket Status",
                    },
                    defaultValue: [],
                    options: () => this.statusList.map((x) => ({ id: x.title, label: x.title })),
                },
                {
                    name: "subject",
                    label: this.$t("ticket.ticketFilterList.subject"),
                    materialIcon: "subject",
                    controlType: "input",
                    controlProps: {},
                    defaultValue: "",
                },
                {
                    name: "email",
                    label: this.$t("ticket.ticketFilterList.email"),
                    materialIcon: "email",
                    controlType: "input",
                    controlProps: {},
                    defaultValue: "",
                },
                {
                    name: "name",
                    label: this.$t("ticket.ticketFilterList.name"),
                    materialIcon: "face",
                    controlType: "input",
                    controlProps: {},
                    defaultValue: "",
                },
                {
                    name: "phone",
                    label: this.$t("ticket.ticketFilterList.phone"),
                    materialIcon: "phone",
                    controlType: "input",
                    controlProps: {},
                    defaultValue: "",
                },
                {
                    name: "types",
                    label: this.$t("ticket.ticketFilterList.type"),
                    materialIcon: "category",
                    controlType: "custom-picker",
                    controlProps: {
                        multiple: true,
                        idKey: "id",
                        labelKey: "label",
                        title: "Select Ticket Tag",
                    },
                    defaultValue: [],
                    options: () => [
                        { id: "instagram-comment", label: this.$t("ticket.igComment") },
                        { id: "instagram-mention", label: this.$t("ticket.igMentioned") },
                        { id: "page-comment", label: "Facebook" },
                        { id: "trendyol", label: "Trendyol" },
                        { id: "form", label: "Supsis Form" },
                        { id: "chatbot", label: this.$t("ticket.incomingReqChatbot") },
                        { id: "offline", label: this.$t("ticket.incomingReqWebForm") },
                        { id: "automation", label: this.$t("ticket.incomingReqAutomation") },
                    ],
                },
                {
                    name: "agents",
                    label: this.$t("ticket.ticketFilterList.assignedUser"),
                    materialIcon: "person",
                    controlType: "agent-picker",
                    controlProps: {
                        addNone: true,
                        addChatbot: false,
                        addBroadcast: false,
                        addAPI: false,
                        addAutomation: false,
                    },
                    defaultValue: [
                        {
                            type: "agent",
                            value: this.$store.state.auth.user?._id,
                        },
                    ],
                },
                {
                    name: "resolved",
                    label: this.$t("wp.completed"),
                    materialIcon: "check_circle",
                    controlType: "custom-picker",
                    controlProps: {
                        multiple: false,
                        idKey: "id",
                        labelKey: "label",
                        title: "Seçim Yap",
                    },
                    defaultValue: null,
                    options: () => [
                        { id: false, label: this.$t("message.no") },
                        { id: true, label: this.$t("message.yes") },
                        { id: null, label: this.$t("message.doesntMatter") },
                    ],
                },
                {
                    name: "channels",
                    label: this.$t("message.channel"),
                    materialIcon: "swap_horiz",
                    controlType: "custom-picker",
                    controlProps: {
                        multiple: true,
                        idKey: "_id",
                        labelKey: "name",
                        title: this.$t("message.makeSelection"),
                    },
                    defaultValue: [],
                    options: () => this.getChannelOptions(),
                },
            ],

            ticketFilter: _.cloneDeep(this.value),
            selectedCustomFilterTemplate: "this-week-open-tickets",
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users.data,
            user: (state) => state.auth.user,
            ticketTags: (state) => state.ticket.topics,
            channels: (state) => state.channel.channels,
        }),
    },
    mounted() {
        this.fetchTopics();
        this.fetchChannels();
    },
    created() {
        if (!this.selectedCustomFilterTemplate) {
            this.applyFilterTemplate(this.defaultFilterTemplate);
        } else {
            this.applyFilterTemplate(this.selectedCustomFilterTemplate);
        }
    },
    methods: {
        ...mapActions({
            fetchTopics: "ticket/fetchTopics",
            fetchChannels: "channel/fetchChannels",
        }),
        getRequiredBreakdowns() {
            const templates = this.getFilterTemplates();
            return templates.find((x) => x.name === this.selectedCustomFilterTemplate)?.requiredBreakdowns || [];
        },
        getFilterTemplates() {
            return [
                {
                    name: "custom",
                    label: this.$t("ticket.ticketFilterList.specialList"),
                    filter: () => ({
                        tags: [],
                        statusList: [],
                        subject: "",
                        email: "",
                        types: [],
                        agents: [],
                        resolved: null,
                        dateFilter: {
                            timeSelect: "custom",
                            startDate: null,
                            endDate: null,
                        },
                    }),
                    requiredFilterNames: [
                        "tags",
                        "statusList",
                        "subject",
                        "email",
                        "name",
                        "phone",
                        "types",
                        "agents",
                        "resolved",
                        "dateFilter",
                    ],
                },
                {
                    name: "this-week-open-tickets",
                    label: this.$t("ticket.ticketFilterList.requestsOpenedThisWeek"),
                    filter: () => ({
                        tags: [],
                        statusList: [],
                        subject: "",
                        email: "",
                        types: [],
                        agents: [],
                        resolved: false,
                        dateFilter: {
                            timeSelect: "isoWeek",
                            startDate: null,
                            endDate: null,
                        },
                    }),
                    requiredFilterNames: ["dateFilter", "resolved"],
                },
                {
                    name: "unassigned-tickets",
                    label: this.$t("ticket.ticketFilterList.unassignedOpenRequests"),
                    filter: () => ({
                        tags: [],
                        statusList: [],
                        subject: "",
                        email: "",
                        types: [],
                        agents: [
                            {
                                type: "none",
                                value: "none",
                            },
                        ],
                        resolved: false,
                        dateFilter: {
                            timeSelect: "custom",
                            startDate: null,
                            endDate: null,
                        },
                    }),
                    requiredFilterNames: ["resolved", "agents"],
                },
                {
                    name: "all-open-tickets",
                    label: this.$t("ticket.ticketFilterList.openRequests"),
                    filter: () => ({
                        tags: [],
                        statusList: [],
                        subject: "",
                        email: "",
                        types: [],
                        agents: [],
                        resolved: false,
                        dateFilter: {
                            timeSelect: "custom",
                            startDate: null,
                            endDate: null,
                        },
                    }),
                    requiredFilterNames: ["resolved"],
                },
                {
                    name: "instagram",
                    label: this.$t("ticket.ticketFilterList.comingFromFacebook"),
                    filter: () => ({
                        tags: [],
                        statusList: [],
                        subject: "",
                        email: "",
                        types: ["instagram-comment", "instagram-mention"],
                        agents: [],
                        resolved: false,
                        dateFilter: {
                            timeSelect: "custom",
                            startDate: null,
                            endDate: null,
                        },
                    }),
                    requiredFilterNames: ["types", "resolved"],
                },
                {
                    name: "form",
                    label: this.$t("ticket.ticketFilterList.supsisFromTheForm"),
                    filter: () => ({
                        tags: [],
                        statusList: [],
                        subject: "",
                        email: "",
                        types: ["form"],
                        agents: [],
                        resolved: false,
                        dateFilter: {
                            timeSelect: "custom",
                            startDate: null,
                            endDate: null,
                        },
                    }),
                    requiredFilterNames: ["types", "resolved"],
                },
                {
                    name: "chatbot",
                    label: "Chatbot",
                    filter: () => ({
                        tags: [],
                        statusList: [],
                        subject: "",
                        email: "",
                        types: ["chatbot"],
                        agents: [],
                        resolved: false,
                        dateFilter: {
                            timeSelect: "custom",
                            startDate: null,
                            endDate: null,
                        },
                    }),
                    requiredFilterNames: ["types", "resolved"],
                },
                {
                    name: "offline",
                    label: "Offline",
                    filter: ()=>({
                        tags: [],
                        statusList: [],
                        subject: "",
                        email: "",
                        types: ["offline"],
                        agents: [],
                        resolved: false,
                        dateFilter: {
                            timeSelect: "custom",
                            startDate: null,
                            endDate: null,
                        },
                    }),
                    requiredFilterNames: ["types", "resolved"],
                },
                {
                    name: "messenger",
                    label: this.$t("ticket.ticketFilterList.comingFromFacebook"),
                    filter: () => ({
                        tags: [],
                        statusList: [],
                        subject: "",
                        email: "",
                        types: ["page-comment"],
                        agents: [],
                        resolved: false,
                        dateFilter: {
                            timeSelect: "custom",
                            startDate: null,
                            endDate: null,
                        },
                    }),
                    requiredFilterNames: ["types", "resolved"],
                },
                {
                    name: "trendyol",
                    label: this.$t("ticket.ticketFilterList.fromTrendyol"),
                    filter: () => ({
                        tags: [],
                        statusList: [],
                        subject: "",
                        email: "",
                        types: ["trendyol"],
                        agents: [],
                        resolved: false,
                        dateFilter: {
                            timeSelect: "custom",
                            startDate: null,
                            endDate: null,
                        },
                    }),
                    requiredFilterNames: ["types", "resolved"],
                },
                {
                    name: "automation",
                    label: this.$t("ticket.ticketFilterList.comesWithAutomation"),
                    filter: () => ({
                        tags: [],
                        statusList: [],
                        subject: "",
                        email: "",
                        types: ["automation"],
                        agents: [],
                        resolved: false,
                        dateFilter: {
                            timeSelect: "custom",
                            startDate: null,
                            endDate: null,
                        },
                    }),
                    requiredFilterNames: ["types", "resolved"],
                },
                {
                    name: "archived",
                    label: this.$t("ticket.ticketFilterList.archivedRequests"),
                    filter: () => ({
                        tags: [],
                        statusList: [],
                        subject: "",
                        email: "",
                        types: [],
                        agents: [],
                        resolved: true,
                        dateFilter: {
                            timeSelect: "custom",
                            startDate: null,
                            endDate: null,
                        },
                    }),
                    requiredFilterNames: ["resolved"],
                },
            ];
        },
        applyFilterTemplate(name) {
            const template = this.getFilterTemplates().find((x) => x.name === name);
            log("applyFilterTemplate", name, template);

            if (template) {
                const filter = template.filter();
                const oldFilters = this.ticketFilter;
                const newFilters = {
                    ...oldFilters,
                    ...filter,
                };
                let requiredFilterNames = template.requiredFilterNames || [];

                const filterMetas = this.filterMetas.filter((x) => requiredFilterNames.includes(x.name));

                let currentBreakdowns = [];
                filterMetas.forEach((meta) => {
                    const item = {
                        ...meta,
                    };
                    if (meta.name === "startDate" || meta.name === "endDate") {
                        item.filterValue = newFilters[meta.name];
                    } else {
                        if (typeof (newFilters[meta.name] === "boolean")) {
                            item.filterValue = newFilters[meta.name];
                        } else {
                            item.filterValue = newFilters[meta.name] || meta.defaultValue;
                        }
                    }
                    currentBreakdowns.push(item);
                });
                log("applyFilterTemplate", {
                    name,
                    filter,
                    requiredFilterNames,
                    filterMetas,
                    currentBreakdowns,
                    newFilters,
                });
                log("updateTicketFilters calling from applyFilterTemplate", {
                    newFilters,
                    oldFilters: this.ticketFilter,
                });
                this.publishDisabled = true;
                this.ticketFilter = _.cloneDeep(currentBreakdowns);
                this.selectedCustomFilterTemplate = name;

                setTimeout(() => {
                    this.publishDisabled = false;
                }, 1000);
            }
        },
        getSelectedFilterTemplateName() {
            const templates = this.getFilterTemplates();
            if (this.selectedCustomFilterTemplate) {
                return templates.find((x) => x.name === this.selectedCustomFilterTemplate)?.label;
            }
            const selectedTemplate = templates.find((x) => {
                const filter = x.filter();
                const isMatched = Object.keys(filter)
                    .map((key) => {
                        const ignoreFields = ["startDate", "endDate"];
                        if (ignoreFields.includes(key)) {
                            return true;
                        }
                        return _.isEqual(filter[key], this.ticketFilter[key]);
                    })
                    .every((key) => {
                        return key;
                    });
                return isMatched;
            });
            return selectedTemplate?.label;
        },
        isClearAllEnabled() {
            return this.ticketFilter.length > 1;
        },
        addFilter(name, value, autoOpen = true) {
            const meta = this.filterMetas.find((x) => x.name === name);
            const filter = this.ticketFilter.find((x) => x.name === name);
            log.info("addFilter", { name, value, meta, filter });
            if (filter) {
                this.ticketFilter = this.ticketFilter.map((x) => {
                    if (x.name === name) {
                        x.filterValue = value || meta.defaultValue;
                    }
                    return x;
                });
            } else {
                this.ticketFilter.push({
                    ...meta,
                    filterValue: value || meta.defaultValue,
                });
            }
            if (autoOpen) {
                setTimeout(() => {
                    const el = document.querySelector(".filter-wrapper-preview[data-filter-name=" + name + "]");
                    if (el) {
                        el.click();
                    }
                }, 100);
            }
        },
        removeFilter(name) {
            log.info("removeFilter", name);
            this.ticketFilter = this.ticketFilter.filter((x) => x.name !== name);
        },
        filterLabelFormatter(name, value) {
            const filter = this.filterMetas.find((x) => x.name === name);
            log.info("filterLabelFormatter", { name, value, filter });
            const isSelect = filter.controlType === "select";

            if (isSelect) {
                const option = filter.options().find((x) => x.id == value);
                if (option) {
                    return `${filter.label}: ${option.label}`;
                } else {
                    return `${filter.label}`;
                }
            } else if (filter.controlType === "custom-picker") {
                if (name === "channels") {
                    const values = Array.isArray(value) ? value : [value];
                    const selectedValues = values
                        .map((channelId) => {
                            const channel = this.channels.find(ch => ch._id === channelId);
                            console.log("channel debug", {
                                channel,
                                channelId,
                                values,
                            });
                            return channel?.name || value;
                        })
                        .filter(Boolean);
                    return `${filter.label}: ${selectedValues.join(", ")}`;
                } else {
                    const values = Array.isArray(value) ? value : [value];
                    const selectedValues = values
                        .map((value) => {
                            const option = filter.options().find((x) => x.id == value);
                            return option?.label || value;
                        })
                        .filter(Boolean);
                    return `${filter.label}: ${selectedValues.join(", ")}`;
                }
            } else if (filter.controlType === "text") {
                return `${filter.label}: ${value}`;
            } else if (filter.controlType === "date-picker") {
                return `${filter.label}: ${moment(value).format("DD MMM YYYY")}`;
            } else if (filter.controlType === "date-range-picker") {
                const shortcuts = {
                    today: this.$t("chatWindow.today"),
                    day: this.$t("chatWindow.today"),
                    "-day": this.$t("chatWindow.yesterday"),
                    isoWeek: this.$t("chatWindow.thisWeek"),
                    "-isoWeek": this.$t("chatWindow.lastWeek"),
                    7: this.$t("chatWindow.last7Days"),
                    30: this.$t("chatWindow.last30Days"),
                    month: this.$t("chatWindow.thisMonth"),
                    "-month": this.$t("chatWindow.lastMonth"),
                    yesterday: this.$t("chatWindow.yesterday"),
                    "last-7-days": this.$t("chatWindow.last7Days"),
                    "last-30-days": this.$t("chatWindow.last30Days"),
                    thisWeek: this.$t("chatWindow.thisWeek"),
                    lastWeek: this.$t("chatWindow.lastWeek"),
                    thisMonth: this.$t("chatWindow.thisMonth"),
                    lastMonth: this.$t("chatWindow.lastMonth"),
                    last24h: this.$t("chatWindow.last24h"),
                    custom: this.$t("chatWindow.custom"),
                    ..._.range(2, 13)
                        .map((i) => ({
                            key: `month${i}`,
                            label: moment().subtract(i, "month").format("MMMM YYYY"),
                        }))
                        .reduce((acc, item) => {
                            acc[item.key] = item.label;
                            return acc;
                        }, {}),
                };
                if (value.timeSelect && value.timeSelect !== "custom") {
                    return `${shortcuts[value.timeSelect]}`;
                } else {
                    const startDate = moment(value.startDate).format("DD MMM YYYY");
                    const endDate = value.endDate ? moment(value.endDate).format("DD MMM YYYY") : "now";

                    if (startDate === endDate) {
                        return `${startDate}`;
                    } else {
                        return `${startDate} - ${endDate}`;
                    }
                }
            } else if (filter.controlType === "agent-picker") {
                if (Array.isArray(value)) {
                    const selectedAgents = value
                        .map((agent) => {
                            if (agent.type === "agent") {
                                const user = this.users.find((x) => x._id === agent.value);
                                return user?._id === this.$store.state.auth.user?._id ? "Ben" : user?.name || "Unknown";
                            }
                        })
                        .filter(Boolean);
                    return `${filter.label}: ${selectedAgents.join(", ")}`;
                }

                return `${filter.label}: ${moment(value).format("DD MMM YYYY")}`;
            }

            if (value) {
                return `${filter.label}:${value}`;
            }
            return filter.label;
        },
        isFilterExists(name) {
            return !!this.ticketFilter.find((x) => x.name === name);
        },
        getChannelOptions() {
            return this.channels.map(channel => ({
                _id: channel._id,
                name: channel.name,
                type: channel.type
            }));
        },
    },
    watch: {
        ticketFilter: {
            handler: function () {
                this.$emit("input", this.ticketFilter);
            },
            deep: true,
        },
    },
};
</script>

<style scoped lang="scss">
.ticket-filter-toolbar {
    display: flex;
    width: 100%;

    .ticket-filter-toolbar {
        margin-bottom: 10px;
    }

    .common-filter-tool {
        border-bottom: none;
        overflow: hidden;
        padding: 0px;

        .channel-picker-inline {
            .groups {
                max-height: 300px !important;
            }
        }

        .filters {
            overflow-x: auto;
            overflow-y: hidden;
            scrollbar-width: none;
        }

        .add-button {
            margin: unset !important;
            border-radius: 20px;
            border: 1px dashed var(--color-border-default);
            font-size: 12px;
            font-weight: 600;
            line-height: 24px;
            padding: 1px 5px !important;
            min-width: fit-content;

            .material-icons {
                font-size: 15px;
                margin-right: 3px;
            }
        }

        .clear-preview {
            font-size: 12px;
            font-weight: 600;
            line-height: 24px;

            .material-icons {
                font-size: 15px;
                margin-right: 3px;
            }
        }

        .filter-wrapper-preview {
            border-radius: 20px;
            padding: 1px 5px !important;
            background: white;
            color: black;
            border: 1px solid var(--color-border-default);

            .filter-value-text {
                font-size: 12px;
                font-weight: 600;
                color: #22272d;
                line-height: 24px;
            }

            .material-icons {
                margin-right: 3px;
            }
        }
    }
}

.active-filters {
    display: flex;
    gap: 5px;
    overflow-x: auto;
    padding: 10px 0;
    overflow-y: hidden;

    &::-webkit-scrollbar {
        display: none;
    }

    .clear-active-filter {
        width: max-content;
        height: 30px;
        background-color: #000;
        color: #fff;
        border-radius: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 7px;
        cursor: pointer;
    }

    .active-filter-group {
        display: flex;
        gap: 5px;
        height: 40px;
    }

    .active-filter {
        display: flex;
        align-items: center;
        background-color: #fff;
        border-radius: 16px;
        padding: 10px 0 10px 10px;
        position: relative;
        width: max-content;
        height: 30px;
        border: 1px solid #d2d2d2;

        .remove-filter {
            cursor: pointer;
            background-color: #fff;
            border-radius: 50%;
            padding: 0 5px;
            margin-left: 5px;
            color: #d2d2d2;
            font-size: 15px;
            font-weight: 900;
        }
    }
}
</style>
